import { parsePhoneNumberWithError, type CountryCode as PhoneCountryCode } from 'libphonenumber-js';
import { isArray, isEmpty, isNil, orderBy, toLower } from 'lodash';
import { type IntlFormatters } from 'react-intl';

import { uniquePush, uniquePushId } from '../arrayutils';
import { getCountryCodeFromDialCode } from '../countryutils';
import { BUILDING_TABLE_NAME, CONTACT_TABLE_NAME } from '../databaseUtils';
import { isNilOrEmpty } from '../objectUtils';
import {
  type Building,
  type BuildingDTO,
  type Contact,
  type ContactDTO,
  type EntityRelation,
  type Lease,
  type LeaseDTO,
  type PhoneNumber,
  type RepaymentStatement,
  type RepaymentStatementDTO,
  type Unit,
  type UnitDTO,
  type UnitOwner,
  type User,
  ContactStatus,
  ContactType,
} from '../types';
import { getMainUnitId } from '../unitutils';

import { EMAIL_REGEX } from './contactValidator';

export enum CUSTOM_VALUES {
  INTEGRATION_SOURCE = 'integration',
  ICLOUD_ETAG = 'ICLOUD:etag',
  ICLOUD_URL = 'ICLOUD:url',
  ICLOUD_DATA = 'ICLOUD:data',
  GOOGLE_ID = 'GOOGLE:id',
  GOOGLE_ETAG = 'GOOGLE:etag',
  MICROSOFT_ID = 'MICROSOFT:id',
  MICROSOFT_ETAG = 'MICROSOFT:etag',
  EXTRA_EMAILS = 'extraEmails',
  ODOO_ID = 'ODOO:odooId',
  ODOO_LAST_UPDATE = 'ODOO:lastUpdate',
  ODOO_NOTES_HTML = 'ODOO:notesHtml',
}

/**
 * Get a displayable contact name doesn't matter it's a company or not
 * @param firstName The first name field
 * @param lastName The last name field
 * @param companyName The company name field
 * @param commercialName The commercial name of the company
 */
export const getContactName = (
  firstName?: string | null,
  lastName?: string | null,
  companyName?: string | null,
  commercialName?: string | null,
  firstNameLast: boolean = false
): string => {
  if (!isNilOrEmpty(commercialName)) {
    return commercialName;
  }
  if (!isNilOrEmpty(companyName)) {
    return companyName;
  }
  const nameArray = firstNameLast
    ? [!isNilOrEmpty(lastName) ? lastName : '', !isNilOrEmpty(firstName) ? firstName : '']
    : [!isNilOrEmpty(firstName) ? firstName : '', !isNilOrEmpty(lastName) ? lastName : ''];

  return nameArray.join(' ').trim();
};

/**
 * Get a displayable contact name from contact object
 * @param contact A contact object containing the firstName, lastName and companyName (all optional)
 */
export const getContactNameFromObject = (
  contact:
    | Pick<ContactDTO, 'firstName' | 'lastName' | 'companyName' | 'commercialName'>
    | Pick<Contact, 'firstName' | 'lastName' | 'companyName' | 'commercialName'>
    | undefined
    | null
): string => {
  if (isNil(contact)) {
    return '';
  }
  return getContactName(contact.firstName, contact.lastName, contact.companyName, contact.commercialName);
};

export const getUserNameFromObject = (
  user: Pick<User, 'firstName' | 'lastName' | 'email'> | undefined | null
): string => {
  if (isNil(user)) {
    return '';
  }

  return getContactName(user.firstName, user.lastName) ?? user.email ?? '';
};

/**
 * Get the contact name in lower case for sortable mean
 * @param contact A contact object containing the firstName, lastName and companyName (all optional)
 */
export const getSortableContactName = (
  contact?: Pick<Contact, 'firstName' | 'lastName' | 'companyName' | 'commercialName'>
): string => {
  if (isNil(contact)) {
    return '';
  }
  return getContactName(
    !isNil(contact.lastName) ? toLower(contact.lastName) : null,
    !isNil(contact.firstName) ? toLower(contact.firstName) : null,
    !isNil(contact.companyName) ? toLower(contact.companyName) : null,
    !isNil(contact.commercialName) ? toLower(contact.commercialName) : null
  );
};

/**
 * Get a displayable string of all tenants separated with comma
 * @param tenants A list of all the tenants
 */
export const getTenantsName = (tenants?: Contact[] | null, firstNameLast: boolean = false): string => {
  return getContactsName(tenants, firstNameLast);
};

/**
 * Get a displayable string of all tenants separated with comma, sorted alphabetically
 * @param tenants A list of all the tenants
 */
export const getSortableTenantsName = (tenants?: Contact[] | null, firstNameLast: boolean = false) => {
  const tenantsWithLowerCaseNames = tenants?.map((tenant) => ({
    ...tenant,
    lastName: !isNil(tenant.lastName) ? toLower(tenant.lastName) : null,
    firstName: !isNil(tenant.firstName) ? toLower(tenant.firstName) : null,
    companyName: !isNil(tenant.companyName) ? toLower(tenant.companyName) : null,
    commercialName: !isNil(tenant.commercialName) ? toLower(tenant.commercialName) : null,
  }));

  return getTenantsName(tenantsWithLowerCaseNames, firstNameLast);
};

/**
 * Get a displayable string of all owners separated with comma
 * @param owners A list of all owners
 */
export const getOwnersName = (owners?: UnitOwner[] | null): string => {
  if (isNil(owners) || isEmpty(owners)) {
    return '';
  }

  return owners.map((owner) => getContactNameFromObject(owner.owner as Contact)).join(', ');
};

/**
 * Get a displayable string of all contact separated with comma
 * @param contacts A list of all the contact
 */
export const getContactsName = (contacts?: Contact[] | ContactDTO[] | null, firstNameLast: boolean = false): string => {
  let contactsName = '';
  if (!isNil(contacts)) {
    contacts.forEach((c: Contact | ContactDTO) => {
      contactsName +=
        (isEmpty(contactsName) ? '' : ', ') +
        getContactName(c.firstName, c.lastName, c.companyName, c.commercialName, firstNameLast);
    });
  }
  return contactsName;
};

/**
 * Get usable email address of all tenants separated with comma
 * @param tenants A list of all the tenants
 */
export const getEmailsOfTenants = (tenants?: Contact[] | null): string => {
  return getEmailsOfContacts(tenants);
};

/**
 * Get usable email address of all contacts separated with comma
 * @param contacts A list of all the contacts
 */
export const getEmailsOfContacts = (contacts?: Contact[] | ContactDTO[] | null): string => {
  let contactsMails = '';
  if (!isNil(contacts)) {
    contacts.forEach((contact: ContactDTO | Contact | null) => {
      const emailToAdd = getEmailOfContact(contact);
      if (!isEmpty(emailToAdd)) {
        contactsMails += (isEmpty(contactsMails) ? '' : ', ') + emailToAdd;
      }
    });
  }
  return contactsMails;
};

export const PREFERRED_INVOICE_EMAIL_LABEL = 'BILLING_EMAIL';

export const getEmailAddressOfContact = (
  contact?: Pick<Contact, 'email' | 'emails'> | Pick<ContactDTO, 'email' | 'emails'> | null,
  preferredLabel?: string
): string => {
  const preferredEmail = preferredLabel
    ? contact?.emails?.find((email) => email.label?.toUpperCase() === preferredLabel.toUpperCase())?.address
    : undefined;

  return preferredEmail ?? contact?.email ?? '';
};

export const contactHasValidEmail = (contact: Contact): boolean => {
  const email = getEmailAddressOfContact(contact);
  return !isNilOrEmpty(email) && EMAIL_REGEX.test(email);
};

export const contactHasMobilePhone = (contact: Contact): boolean =>
  !isNil(contact.phoneNumbers) && contact.phoneNumbers.some((phoneNumber) => !isNilOrEmpty(phoneNumber.number));

export const contactHasPostalAddress = (contact: Contact): boolean => {
  const address = contact.address;
  if (isNil(address)) {
    return false;
  }
  return !isNilOrEmpty(address.street) && !isNilOrEmpty(address.postalCode) && !isNilOrEmpty(address.city);
};

export const getEmailOfContact = (contact?: Contact | ContactDTO | null, preferredLabel?: string): string => {
  const contactName = getContactNameFromObject(contact);
  const email = getEmailAddressOfContact(contact, preferredLabel);
  return isNilOrEmpty(email) ? '' : `${contactName} <${email}>`;
};

/**
 * Get usable first phone numbers of all contacts separated with comma
 * @param contacts A list of all the contacts
 */
export const getPhoneNumbersOfContacts = (contacts?: Contact[] | ContactDTO[] | null): string => {
  let contactsPhoneNumbers = '';
  if (!isNil(contacts)) {
    contacts.forEach((contact: ContactDTO | Contact) => {
      contactsPhoneNumbers +=
        (isEmpty(contactsPhoneNumbers) || isNilOrEmpty(contact.phoneNumbers) ? '' : ', ') +
        (isNilOrEmpty(contact.phoneNumbers)
          ? ''
          : getContactNameFromObject(contact).replace(',', '') +
            ` <${formatPrimaryPhoneNumber(contact.phoneNumbers)}>`);
    });
  }
  return contactsPhoneNumbers;
};

export const getPrimaryPhoneNumber = (phoneNumbers?: PhoneNumber[] | null): PhoneNumber | undefined =>
  phoneNumbers?.find((phoneNumber) => phoneNumber.isPrimary);

/**
 * Get the first displayable phone number from the list
 * @param phoneNumbers The list of phoneNumbers
 */
export const formatPrimaryPhoneNumber = (phoneNumbers?: PhoneNumber[] | null): string => {
  if (isNil(phoneNumbers)) {
    return '';
  }
  const primaryPhoneNumber = getPrimaryPhoneNumber(phoneNumbers);
  return formatPhoneNumber(primaryPhoneNumber ?? null);
};

export const formatPhoneNumbers = (phoneNumbers?: PhoneNumber[] | null): string =>
  phoneNumbers
    ?.reduce<string[]>((acc: string[], currentPhoneNumber) => {
      const formattedNumber = formatPhoneNumber(currentPhoneNumber);
      if (!isEmpty(formattedNumber)) acc.push(formattedNumber);
      return acc;
    }, [])
    .join(', ') ?? '';

export const formatInternationalPhoneNumber = (phoneNumber?: string | null): string => {
  if (isNilOrEmpty(phoneNumber)) {
    return '';
  }
  try {
    return parsePhoneNumberWithError(phoneNumber).formatInternational();
  } catch {
    return phoneNumber;
  }
};

const formatPhoneNumber = (phoneNumber: PhoneNumber | null): string => {
  if (isNil(phoneNumber)) {
    return '';
  }
  try {
    return parsePhoneNumberWithError(
      phoneNumber.number,
      getCountryCodeFromDialCode(phoneNumber.countryCode) as PhoneCountryCode
    ).formatInternational();
  } catch {
    return '';
  }
};

export const getAddressIdOfContacts = (contacts?: Contact[] | ContactDTO[] | null) => {
  return contacts?.[0]?.addressId ?? '';
};

export const isContactInList = <
  T extends {
    contactId: string;
  },
>(
  value: T,
  list: T[]
) => {
  return !isNil(list.find((v) => v.contactId === value.contactId));
};

export const getContactTypes = (
  contact: Contact | ContactDTO,
  formatMessage: IntlFormatters['formatMessage']
): string => {
  return getPrintableContactTypes(contact.types, formatMessage);
};

const getPrintableContactTypes = (types: ContactType[], formatMessage: IntlFormatters['formatMessage']): string => {
  return (types as string[])
    .map((type: string) => formatMessage({ id: `contact.type.${type.toLowerCase()}` }))
    .join(', ');
};

export const contactContainsOnlyType = (contact: Contact | ContactDTO, type: ContactType) =>
  contact.types.length === 1 && contactContainsType(contact, type);

export const contactContainsType = (contact: Pick<Contact, 'types'>, type: ContactType) =>
  typesContainsType(contact.types, type);

export const contactContainsTypes = (contact: Contact | ContactDTO, types: ContactType[]) => {
  return types.some((type: ContactType | ContactType) => contactContainsType(contact, type));
};

export const typesContainsType = (types: ContactType[], type: ContactType) => {
  return types.some((contactType: ContactType) => contactType === type);
};

export const isOneTypeOverlapping = (types: ContactType[], typesCompare: ContactType[]) => {
  return types.some((contactType: ContactType) => typesContainsType(typesCompare, contactType));
};

const CHARGE_SENDER_CONTACT_TYPES = [ContactType.CONTRACTOR, ContactType.HOA];

/** A SENDER alias points at a supplier: a contractor, or the association of co-owners a syndic writes for. */
export const isChargeSenderContactTypes = (types: ContactType[]): boolean =>
  isOneTypeOverlapping(types, CHARGE_SENDER_CONTACT_TYPES);

export const contactMostImportantType = (contact: Contact | ContactDTO): ContactType => {
  const priorityOrder = [
    ContactType.CLIENT,
    ContactType.MEMBER,
    ContactType.OWNER,
    ContactType.JOINT_OWNERSHIP,
    ContactType.CONTRACTOR,
    ContactType.TENANT,
    ContactType.GUARANTOR,
    ContactType.OTHER,
  ];

  return ((contact.types as ContactType[]) ?? []).reduce((result, currentType) => {
    if (!result || priorityOrder.indexOf(currentType) < priorityOrder.indexOf(result)) {
      return currentType;
    }
    return result;
  }, ContactType.OTHER);
};

export const isValidContact = (contacts: Contact): boolean =>
  ![ContactStatus.ERROR, ContactStatus.ARCHIVED].includes(contacts.status as ContactStatus);

export const getValidContacts = (contacts: Contact[]): Contact[] => contacts.filter(isValidContact);

export function stringToColor(str: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let colour = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += `00${value.toString(16)}`.substr(-2);
  }
  /* eslint-enable no-bitwise */

  return colour;
}

export const getFirstLettersUppercase = (str: string) => {
  const strippedString = str.replace(/\(.*?\)|["\\-]/g, '').trim();
  const firstThreeWords = strippedString.split(' ').slice(0, 3);
  const firstThreeInitials = firstThreeWords.map((word) => word.charAt(0).toUpperCase());

  return firstThreeInitials.join('');
};

export const getUnitsOfContact = async (
  contactId: string,
  getContact: (ownerId: string) => Promise<ContactDTO | undefined>,
  getBuilding: (buildingId: string) => Promise<BuildingDTO | undefined>,
  getUnit: (unitId: string) => Promise<UnitDTO | undefined>
) => {
  const currentContact = await getContact(contactId);
  if (!currentContact) {
    return null;
  }

  return await getUnitsOfContactObject(currentContact, getContact, getBuilding, getUnit);
};

export const getUnitsOfContactObject = async (
  contactWithUnitAndBuildingOwners: ContactDTO,
  getContact: (ownerId: string) => Promise<ContactDTO | undefined>,
  getBuilding: (buildingId: string) => Promise<BuildingDTO | undefined>,
  getUnit: (unitId: string) => Promise<UnitDTO | undefined>
) => {
  let allUnitsIds: string[] = [];
  const isJointOwner = contactContainsType(contactWithUnitAndBuildingOwners, ContactType.JOINT_OWNERSHIP);
  const isOwner = contactContainsType(contactWithUnitAndBuildingOwners, ContactType.OWNER);
  if (isJointOwner) {
    // If is a joint ownership, just get one of its owner and fetch all its units from joint ownership
    const oneOwnerIdOfJointOwnership = contactWithUnitAndBuildingOwners.jointOwners
      ? contactWithUnitAndBuildingOwners.jointOwners[0]?.contactId
      : undefined;
    if (oneOwnerIdOfJointOwnership) {
      const completeOwnerOfJointOwnership = await getContact(oneOwnerIdOfJointOwnership);
      if (completeOwnerOfJointOwnership) {
        const unitsIdsOfOwner = await getUnitsIdsOfOwnerOfJointOwnership(
          completeOwnerOfJointOwnership,
          getBuilding,
          getUnit,
          contactWithUnitAndBuildingOwners.id
        );
        allUnitsIds = unitsIdsOfOwner;
      }
    }
  } else if (isOwner) {
    allUnitsIds = await getUnitsIdsOfOwner(contactWithUnitAndBuildingOwners, getBuilding);
  }
  const completeUnitPromises = allUnitsIds.map((unitId) => getUnit(unitId));
  return await Promise.all(completeUnitPromises);
};

const getUnitsIdsOfOwner = async (
  owner: ContactDTO,
  getBuilding: (buildingId: string) => Promise<BuildingDTO | undefined>
) => {
  const ownerUnitsIds: string[] = [];
  // Keep only the buildings owned at 100% by the owner
  const buildingOwners = owner?.buildings?.items?.filter((bo) => bo && bo.stake === 100) ?? [];
  const buildingsPromises = buildingOwners.map((buildingOwner) => getBuilding(buildingOwner!.buildingId!));
  const completeBuildings = await Promise.all(buildingsPromises);
  completeBuildings.forEach((currentBuilding) => {
    const buildingUnits = currentBuilding?.units?.items?.filter((u) => !isNil(u)) ?? [];
    buildingUnits.forEach((u) => {
      if (u) {
        uniquePush(ownerUnitsIds, u.id!);
      }
    });
  });
  // Keep only the units owned at 100% by the owner
  const ownerUnits = owner?.units?.items?.filter((uo) => uo && uo.stake === 100) ?? [];
  ownerUnits.forEach((uo) => {
    if (uo && uo.unitId) {
      uniquePush(ownerUnitsIds, uo.unitId);
    }
  });
  return ownerUnitsIds;
};

const getUnitsIdsOfOwnerOfJointOwnership = async (
  owner: ContactDTO,
  getBuilding: (buildingId: string) => Promise<BuildingDTO | undefined>,
  getUnit: (unitId: string) => Promise<UnitDTO | undefined>,
  jointOwnershipId: string
) => {
  const ownerUnitsIds: string[] = [];
  // Keep only the buildings owned at less than 100% by the owner
  const buildingOwners = owner?.buildings?.items?.filter((bo) => bo && bo.stake !== 100) ?? [];
  const buildingsPromises = buildingOwners.map((buildingOwner) => getBuilding(buildingOwner!.buildingId!));
  const completeBuildings = await Promise.all(buildingsPromises);
  completeBuildings.forEach((currentBuilding) => {
    // Keep only the buildings on the joint ownership
    if (currentBuilding && currentBuilding.jointOwnershipId === jointOwnershipId) {
      const buildingUnits = currentBuilding?.units?.items?.filter((u) => !isNil(u)) ?? [];
      buildingUnits.forEach((u) => {
        if (u) {
          uniquePush(ownerUnitsIds, u.id!);
        }
      });
    }
  });
  // Keep only the units owned at 100% by the owner
  const ownerUnitsPromises: Promise<UnitDTO | undefined>[] =
    owner?.units?.items?.reduce((acc: Promise<UnitDTO | undefined>[], uo) => {
      if (uo && uo.stake !== 100) {
        acc.push(getUnit(uo.unitId!));
      }
      return acc;
    }, []) ?? [];
  const ownerUnits = await Promise.all(ownerUnitsPromises);
  ownerUnits.forEach((u) => {
    if (u && u.jointOwnershipId === jointOwnershipId) {
      uniquePush(ownerUnitsIds, u.id!);
    }
  });
  return ownerUnitsIds;
};

/**
 * Returns units of owner. Owner can be joint ownership or simple owner
 * @param owner
 * @param buildings
 * @param units
 * @param getBuilding
 * @param getUnit
 * @returns List of units or empty list
 */
export const getUnitsOfOwnerSync = (owner: Contact, buildings: Building[], units: Unit[]) => {
  const allUnitIds: string[] = [];
  // Get all units of his building
  if (contactContainsType(owner, ContactType.JOINT_OWNERSHIP)) {
    // Building units of joint owerships
    const buildingOfJointOwnership: Building[] = getBuildingsOfJointOwnership(buildings, owner.id);
    buildingOfJointOwnership.forEach((currentBuilding) => {
      if (currentBuilding.units) {
        allUnitIds.push(...currentBuilding.units.map((u) => u.id));
      }
    });
    // Units of joint owerships
    const unitsOfJointOwnership = getUnitsOfJointOwnership(units, owner.id);
    unitsOfJointOwnership.forEach((unit) => {
      uniquePush(allUnitIds, unit.id);
    });
  } else if (contactContainsTypes(owner, [ContactType.OWNER, ContactType.CLIENT])) {
    // Building units owner at 100% of owner
    owner.buildings?.forEach((buildingOwner) => {
      if (buildingOwner.stake === 100) {
        const currentBuilding = buildings.find((building) => building.id === buildingOwner.buildingId);
        if (currentBuilding && currentBuilding.units) {
          allUnitIds.push(...currentBuilding.units.map((u) => u.id));
        }
      }
    });
    // Units owner at 100% of owner
    owner.units?.forEach((unitOwner) => {
      if (unitOwner.unit && unitOwner.stake === 100) {
        uniquePush(allUnitIds, unitOwner.unit.id);
      }
    });
  }

  const unitsOfOwner: Unit[] = allUnitIds.reduce((result, currentUnitId) => {
    const unit = units.find((u) => u.id === currentUnitId);
    if (!unit) {
      return result;
    }
    result.push(unit);
    return result;
  }, [] as Unit[]);

  return unitsOfOwner;
};

export const getNumberOfUnitsForOwner = (owner: Contact, buildings: Building[], units: Unit[]) => {
  return getUnitsOfOwnerSync(owner, buildings, units).length;
};

export interface RepaymentsGroupedByBuildingsUnits {
  buildingsGroupName: string;
  unitsGroupName: string;
  repayments: (RepaymentStatement | RepaymentStatementDTO)[];
}

const getBuildingsOfUnits = (units: Unit[]): Building[] => {
  return units.reduce((acc: Building[], unit) => {
    const building = unit.building;
    if (building) {
      uniquePushId(acc, building);
    }
    return acc;
  }, []);
};

export const getBuildingsOfJointOwnership = (buildings: Building[], jointOwnershipId: string) => {
  return buildings.filter((building) => building.jointOwnershipId && building.jointOwnershipId === jointOwnershipId);
};

export const getUnitsOfJointOwnership = (units: Unit[], jointOwnershipId: string) => {
  return units.filter((unit) => unit.jointOwnershipId && unit.jointOwnershipId === jointOwnershipId);
};

const isHoaBuildingRelation = (relation: EntityRelation) =>
  relation.sourceTableName === CONTACT_TABLE_NAME && relation.foreignTableName === BUILDING_TABLE_NAME;

export const isContactToContactRelation = (relation: EntityRelation) =>
  relation.sourceTableName === CONTACT_TABLE_NAME && relation.foreignTableName === CONTACT_TABLE_NAME;

export const getHoaBuildingRelationsOfHoa = (relations: EntityRelation[], hoaContactId: string) =>
  relations.filter((relation) => isHoaBuildingRelation(relation) && relation.sourceId === hoaContactId);

export const getBuildingIdsOfHoa = (relations: EntityRelation[], hoaContactId: string) =>
  getHoaBuildingRelationsOfHoa(relations, hoaContactId).map((relation) => relation.foreignId);

export const getBuildingsOfHoa = (buildings: Building[], relations: EntityRelation[], hoaContactId: string) => {
  const buildingIds = getBuildingIdsOfHoa(relations, hoaContactId);
  return buildings.filter((building) => buildingIds.includes(building.id));
};

export const getHoaBuildingRelationOfBuilding = (relations: EntityRelation[], buildingId: string) =>
  relations.find((relation) => isHoaBuildingRelation(relation) && relation.foreignId === buildingId);

export const getBuildingsOfOwner = (buildings: Building[], owner: Contact) => {
  return (
    owner.buildings?.reduce((result: Building[], currentBuildingOwner) => {
      const building = buildings.find((currentBuilding) => currentBuilding.id === currentBuildingOwner.buildingId);
      if (!building) {
        return result;
      }
      result.push(building);
      return result;
    }, []) || []
  );
};

const getUnitsGroupName = (units: Unit[], mainUnitId: string) => {
  const sortedUnits = orderBy(units, [(unit) => (unit.id === mainUnitId ? 0 : 1), 'name'], ['asc', 'asc']);
  return sortedUnits.map((unit) => unit.name).join(', ');
};

const getBuildingsGroupName = (buildings: Building[]) => {
  const buildingNames = buildings.map((b) => b.name);
  return buildingNames.sort().join(', ');
};

export const groupRepaymentsByBuildingsAndUnits = async (
  repayments: (RepaymentStatement | RepaymentStatementDTO)[],
  getLeaseWithUnitLease: (id: string) => Lease | undefined | null | Promise<LeaseDTO | undefined | null>,
  getUnitWithBuilding: (id: string) => Unit | undefined | Promise<UnitDTO | undefined | null>
): Promise<RepaymentsGroupedByBuildingsUnits[]> => {
  const groupedRepayments: RepaymentsGroupedByBuildingsUnits[] = [];
  for (const repayment of repayments) {
    const completeLease = await Promise.resolve(getLeaseWithUnitLease(repayment.leaseId));
    if (completeLease) {
      const mainUnitId = getMainUnitId(completeLease);
      const completeUnits = await Promise.resolve(getCompleteUnitsOfLease(completeLease, getUnitWithBuilding));
      const unitsGroupName = getUnitsGroupName(completeUnits, mainUnitId ?? '');
      const unitBuildings = getBuildingsOfUnits(completeUnits);
      const buildingsGroupName = getBuildingsGroupName(unitBuildings);

      const groupIndex = groupedRepayments.findIndex(
        (group) => group.unitsGroupName === unitsGroupName && group.buildingsGroupName === buildingsGroupName
      );
      const repaymentWithLease = { ...repayment, lease: completeLease } as RepaymentStatement | RepaymentStatementDTO;
      if (groupIndex > -1) {
        groupedRepayments[groupIndex].repayments.push(repaymentWithLease);
      } else {
        groupedRepayments.push({
          unitsGroupName,
          buildingsGroupName,
          repayments: [repaymentWithLease],
        });
      }
    }
  }

  return groupedRepayments;
};

const getCompleteUnitsOfLease = async (
  lease: Lease | LeaseDTO,
  getUnitWithBuilding: (id: string) => Unit | undefined | Promise<UnitDTO | undefined | null>
) => {
  if (!lease.units) {
    return [];
  }

  const unitLeases = isArray(lease.units) ? lease.units : (lease as LeaseDTO).units!.items;

  const unitsResults = await Promise.all(
    unitLeases.map(async (unitLease) => {
      if (!isNilOrEmpty(unitLease?.unit?.building?.name)) {
        return unitLease?.unit; // If we already have the whole needed object
      }

      return unitLease?.unitId ? await Promise.resolve(getUnitWithBuilding(unitLease.unitId)) : undefined;
    })
  );

  return unitsResults.filter((unit): unit is Unit => !isNil(unit));
};

export const compareContactsByName = (a: Contact, b: Contact): number => {
  const nameToCompare1 = !isNil(a.companyName)
    ? a.companyName.toLowerCase()
    : `${!isNil(a.firstName) ? a.firstName : ''}${!isNil(a.lastName) ? a.lastName : ''}`.toLowerCase();
  const nameToCompare2 = !isNil(b.companyName)
    ? b.companyName.toLowerCase()
    : `${!isNil(b.firstName) ? b.firstName : ''}${!isNil(b.lastName) ? b.lastName : ''}`.toLowerCase();

  if (nameToCompare1 < nameToCompare2) {
    return -1;
  }
  if (nameToCompare1 > nameToCompare2) {
    return 1;
  }
  return 0;
};

export const getOwnerNameOfUnitOrBuilding = (unit: UnitDTO, ownersAndJointOwners: (ContactDTO | Contact)[]) => {
  // Unit with jointownership
  if (unit.jointOwnershipId) {
    const jointOwner = ownersAndJointOwners.find((owner) => owner.id === unit.jointOwnershipId);
    return getContactNameFromObject(jointOwner as Contact);
  }
  // Simple owner on unit?
  if (unit.owners && unit.owners.items && unit.owners.items.length > 0) {
    const owners = unit.owners.items.map((owner) => ownersAndJointOwners.find((o) => o.id === owner?.ownerId));
    return owners.map((owner) => getContactNameFromObject(owner)).join(', ');
  }

  if (unit.building?.jointOwnershipId) {
    const buildingJointOwner = ownersAndJointOwners.find((owner) => owner.id === unit.building?.jointOwnershipId);
    return getContactNameFromObject(buildingJointOwner);
  }

  if (unit.building && unit.building.owners && unit.building.owners.items && unit.building.owners.items.length > 0) {
    const owners = unit.building.owners.items.map((owner) =>
      ownersAndJointOwners.find((o) => o.id === owner?.contactId)
    );
    return owners.map((owner) => getContactNameFromObject(owner)).join(', ');
  }

  return '';
};

export const ownersToCustomComboItem = (owners: Contact[]) => {
  return owners.map((owner) => {
    const name = getContactNameFromObject(owner);
    const color = stringToColor(name);
    return {
      name,
      color,
      value: owner.id,
      type: contactMostImportantType(owner),
    };
  });
};

export const ownerIdsToCustomComboItem = (ids: string[], orderedCandidates: Contact[] | undefined) => {
  if (isNil(orderedCandidates)) {
    return [];
  }
  const finalCandidates = ids.reduce((acc, currentId) => {
    const potentialContactById = orderedCandidates.find((candidate) => candidate.id === currentId);
    if (!isNil(potentialContactById)) acc.push(potentialContactById);
    return acc;
  }, [] as Contact[]);
  return ownersToCustomComboItem(finalCandidates);
};

export const getOrderedCandidates = (contacts: Contact[], types: ContactType[]) => {
  const candidates = contacts.filter((c) => contactContainsTypes(c, types));
  const orderedCandidates = candidates.sort((candidate1, candidate2) => {
    if (contactMostImportantType(candidate1) < contactMostImportantType(candidate2)) return -1;
    if (contactMostImportantType(candidate1) >= contactMostImportantType(candidate2)) return 1;
    return 0;
  });

  return { candidates, orderedCandidates };
};

/**
 * It returns the invoice sender id of the unit or the building if owner are at building level
 * @param unit The corresponding unit
 * @param getBuilding Function to get the building from id
 * @returns The id of the owner (or joint owner)
 */
export const getInvoiceSenderIdOfUnitOrBuilding = (
  unit: Unit,
  getBuilding: (id: string) => Building | undefined
): string => {
  if (unit.jointOwnershipId) {
    // Unit with joint ownership
    return unit.jointOwnershipId;
  }
  if (unit.owners && unit.owners.length > 0) {
    // Simple owner on unit
    return unit.owners[0].owner?.id ?? '';
  }

  const building = getBuilding(unit.building?.id ?? '');
  if (building?.jointOwnershipId) {
    // Building with joint ownership
    return building?.jointOwnershipId;
  }

  if (building && building.owners && building.owners.length > 0) {
    // Single owner on building
    return building.owners[0].owner?.id ?? '';
  }

  return '';
};

export const getContactIntegrationSource = (contact: ContactDTO | Contact) =>
  contact.customValues?.find((customValue) => customValue.name === CUSTOM_VALUES.INTEGRATION_SOURCE)?.value;

export const getUserIdsFromContacts = (contactIds: string[], users: User[]) =>
  contactIds.reduce<string[]>((acc, contactId) => {
    const user = users.find((currentUser) => currentUser.contactId === contactId);
    if (user) {
      acc.push(user.id);
    }
    return acc;
  }, []);
